import type { FC } from "react"
import { useForm } from "react-hook-form"

import type { AdjustmentHistorySearchParams } from "../../schemas"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type {
  InventoryAdjustmentReason,
  InventoryAdjustmentStatus,
  InventoryAdjustmentType,
} from "@/graphql/codegen/graphql"
import {
  InventoryDateRangeFilter,
  InventoryFilterList,
  InventoryFilterSearchableList,
} from "@/screens/Inventory/components"
import type { Option } from "@/types/form-fields"

interface AdjustmentHistoryFilterDrawerProps {
  open: boolean
  searchParams: AdjustmentHistorySearchParams
  adjustmentTypeOptions: Option<InventoryAdjustmentType>[]
  statusOptions: Option<InventoryAdjustmentStatus>[]
  reasonOptions: Option<InventoryAdjustmentReason>[]
  warehouseOptions: Option<string>[]
  adjustedByOptions: Option<string>[]
  onToggle: () => void
  onChange: <K extends keyof AdjustmentHistorySearchParams>(key: K, value: AdjustmentHistorySearchParams[K]) => void
  onClear: () => void
}

export const AdjustmentHistoryFilterDrawer: FC<AdjustmentHistoryFilterDrawerProps> = ({
  open,
  searchParams,
  adjustmentTypeOptions,
  statusOptions,
  reasonOptions,
  warehouseOptions,
  adjustedByOptions,
  onToggle,
  onChange,
  onClear,
}) => {
  const adjustmentTypes = searchParams.adjustmentTypes ?? []
  const statuses = searchParams.statuses ?? []
  const adjustmentReasons = searchParams.adjustmentReasons ?? []
  const warehouseAreaIds = searchParams.warehouseAreaIds ?? []
  const adjustedByIds = searchParams.adjustedByIds ?? []
  const dateFrom = searchParams.dateFrom ?? null
  const dateTo = searchParams.dateTo ?? null

  const { control } = useForm({
    defaultValues: { adjustmentTypes, statuses, adjustmentReasons, warehouseAreaIds, adjustedByIds },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='adjustment-history.filter-drawer'>
      <div className='flex h-full flex-col gap-y-4 overflow-y-auto pb-4'>
        <InventoryDateRangeFilter
          label='Date Range'
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={(value) => onChange("dateFrom", value)}
          onDateToChange={(value) => onChange("dateTo", value)}
          testId='adjustment-history.date-range-filter'
        />

        <InventoryFilterSearchableList
          label='Warehouse'
          control={control}
          name='warehouseAreaIds'
          values={warehouseAreaIds}
          options={warehouseOptions}
          onChange={(values) => onChange("warehouseAreaIds", values)}
          testId='adjustment-history.warehouse-filter'
        />

        <InventoryFilterList
          label='Adjustment Type'
          control={control}
          name='adjustmentTypes'
          values={adjustmentTypes}
          options={adjustmentTypeOptions}
          onChange={(values) => onChange("adjustmentTypes", values)}
          testId='adjustment-history.adjustment-type-filter'
        />

        <InventoryFilterList
          label='Reason'
          control={control}
          name='adjustmentReasons'
          values={adjustmentReasons}
          options={reasonOptions}
          onChange={(values) => onChange("adjustmentReasons", values)}
          testId='adjustment-history.reason-filter'
        />

        <InventoryFilterSearchableList
          label='Adjusted By'
          control={control}
          name='adjustedByIds'
          values={adjustedByIds}
          options={adjustedByOptions}
          onChange={(values) => onChange("adjustedByIds", values)}
          testId='adjustment-history.adjusted-by-filter'
        />

        <InventoryFilterList
          label='Transaction Status'
          control={control}
          name='statuses'
          values={statuses}
          options={statusOptions}
          onChange={(values) => onChange("statuses", values)}
          testId='adjustment-history.status-filter'
        />
      </div>
    </SearchFilterDrawer>
  )
}
